import React from "react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { isCapabilityLive } from "@/storefront/registry";

/** L2-003: a raw capability enum value ("service_visit") must never reach the page, even for a
 * capability nobody has added a translation for yet — that is a missing-i18n-key bug, and it
 * should be loud in dev (console.warn) rather than silently shipped to a citizen as-is. Turns
 * "service_visit" into "Service visit": a humanised label is still wrong copy, but it reads as a
 * feature name, not as leaked internal wiring. */
function humanise(key) {
  const words = String(key).split("_").filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function capabilityLabel(capabilityLabels, key) {
  if (capabilityLabels[key]) return capabilityLabels[key];
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`AboutSection: no capabilityLabels translation for "${key}" — add it to en.js/hi.js.`);
  }
  return humanise(key);
}

/** `profile.highlights` (owner-entered, ≤3, schema in `directory/routes.py`'s `ProfileConfigIn`)
 * plus a REAL rating/review-count tile when the business has reviews — never a placeholder or an
 * invented number (`STOREFRONT_BRIEF.md`'s owner decisions). Matches the handoff's
 * "27 yrs / 4.6★ / 30 min avg delivery" row under the About copy (Storefront S1, B1). */
function statTiles(business, t, lang) {
  const highlights = business.storefront_config?.profile?.highlights || [];
  // RF-20: optional Hindi value/label per tile, English when blank.
  const tiles = highlights.map((h) => ({
    value: (lang === "hi" && h.value_hi) || h.value,
    label: (lang === "hi" && h.label_hi) || h.label,
  }));
  if (business.rating_count > 0) {
    tiles.unshift({ value: `${Number(business.rating_avg).toFixed(1)}★`, label: t("storefront.ratingStatLabel") });
  }
  return tiles;
}

/** The description + capability-badge block that used to be the "info" tab — not in the PR3
 * brief's component list (the design floor names it a PSEUDO "about" section, ARCHITECTURE.md
 * §5), but it's real content on the current page and always present regardless of capabilities,
 * so parity requires keeping it. Unconditional, like the old "info" tab was. Stat tiles (B1) sit
 * at the top of this column, directly under the About copy, per the handoff layout — not at the
 * bottom of the page. */
export function AboutSection({ business }) {
  const { t, lang } = useApp();
  const capabilityLabels = t("business.capabilityLabels");
  const tiles = statTiles(business, t, lang);
  // UAT P2x: no live capability -> no "Services offered" heading over an empty row.
  // RF-10: "Enquiry" is a contact form, not a service — on its own it was a stray pill under the
  // heading. It still badges next to real services (a clinic's "Appointments · Enquiry").
  const live = (business.capabilities || []).filter((c) => isCapabilityLive(business, c));
  const liveCapabilities = live.length === 1 && live[0] === "enquiry" ? [] : live;
  return (
    <div className="space-y-6" data-testid="storefront-section-about">
      <div>
        <h2 className="font-heading text-xl font-semibold">{t("common.about")}</h2>
        <p className="mt-2 leading-relaxed text-muted-foreground">{localized(lang, business, "description")}</p>
        {tiles.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-3" data-testid="storefront-about-stats">
            {tiles.map((tile, i) => (
              <div
                key={i}
                data-testid={`storefront-about-stat-${i}`}
                className="rounded-2xl border border-border bg-[hsl(var(--primary))]/5 px-3 py-3 text-center"
              >
                <p className="font-heading text-lg font-semibold text-[hsl(var(--primary))]">{tile.value}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{tile.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      {liveCapabilities.length > 0 && (
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("business.capabilities")}
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {/* W-02: a capability the business toggled on but can't actually offer publicly on its
           * current plan (e.g. "ordering" on Free) must not badge here — this page was promising
           * "Online ordering" while the matching section was correctly hidden a few lines below. */}
          {liveCapabilities.map((c) => (
            <span
              key={c}
              data-testid={`capability-${c}`}
              className="rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3 py-1 text-xs font-medium text-[hsl(var(--primary))]"
            >
              {capabilityLabel(capabilityLabels, c)}
            </span>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
